import { container } from '@databases/service-container';
import { createListenerMiddleware } from '@reduxjs/toolkit';
import { debounce } from 'lodash-es';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { type AppRootState } from '../store';

import { hasRelevantStateChanged } from './utils/has-relevant-state-changed';
import { isReferencing } from './utils/is-referencing';
import { shouldShowActionBar } from './utils/should-show-action-bar';

export const actionBarMiddleware = createListenerMiddleware();

const debouncedDispatchActionBar = debounce((showActionBar: boolean) => {
  const eventBus = container.resolve('EventBus');

  if (showActionBar) {
    eventBus.dispatch('showActionBar', null);
  } else {
    eventBus.dispatch('hideActionBar', null);
  }
}, 100);

/**
 * We could have one listener for showing the action bar and one for hiding
 * the action bar, but that would result in duplicate logic. So, we opt to do
 * everything in the predicate instead of in the effect.
 */
actionBarMiddleware.startListening({
  predicate: (action, currentState, previousState) => {
    const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
      'databases-frontend-visual-refresh-table-m1'
    );
    const isNativeEmbedEnabled = isFeatureFlagEnabled(
      'database-frontend-native-embed-bridge'
    );

    if (!isVisualRefreshM1Enabled || !isNativeEmbedEnabled || isReferencing()) {
      return false;
    }

    if (
      !hasRelevantStateChanged(
        currentState as AppRootState,
        previousState as AppRootState
      )
    ) {
      return false;
    }

    const state = currentState as AppRootState;
    const showActionBar = shouldShowActionBar(state);

    debouncedDispatchActionBar(showActionBar);

    return false;
  },
  effect: () => {},
});
